import React, { useEffect, useState, useRef } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import emailjs from "@emailjs/browser";
import Notiflix from "notiflix";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  Building2,
  Sparkles,
  ArrowRight,
  User,
  Box,
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import CountryCodeDropdown from "../components/CountryCodeDropdown";
import { Helmet } from "react-helmet";
import ReCAPTCHA from "react-google-recaptcha";
import { countryCodes } from "../data/countryCodes";

const Contact = () => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    country: "",
    quantity: "",
    oilType: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isHuman, setIsHuman] = useState(false);
  const [countryCode, setCountryCode] = useState("+216"); // Default Tunisia
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    const handleScroll = () => {
      setIsVisible(window.pageYOffset > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Input handler
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Phone should be at least 7 digits
    return phone.replace(/\D/g, "").length >= 7;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number (at least 7 digits)";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company is required";
    }

    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!formData.quantity.trim()) {
      newErrors.quantity = "Quantity is required";
    } else if (isNaN(formData.quantity) || parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = "Please enter a valid quantity (greater than 0)";
    }

    if (!formData.oilType.trim()) {
      newErrors.oilType = "Type of olive oil is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    if (!isHuman) {
      newErrors.isHuman = "Please complete the reCAPTCHA verification";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Show first error as notification
      const firstError = Object.values(newErrors)[0];
      Notiflix.Notify.failure(firstError, {
        position: "right-top",
        distance: "30px",
        fontSize: "16px",
        width: "380px",
        cssAnimationStyle: "zoom",
        cssAnimationDuration: 400,
        timeout: 4000,
        useIcon: true,
        borderRadius: "12px",
      });

      // Scroll to first error field
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
      return false;
    }

    setErrors({});
    return true;
  };

  const clearForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      country: "",
      quantity: "",
      oilType: "",
      message: "",
    });
    setErrors({});
    setIsHuman(false);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitSuccess(false);

    const templateParams = {
      from_name: `${formData.firstName} ${formData.lastName}`,
      from_email: formData.email,
      phone_number: countryCode + formData.phone.replace(/\D/g, ""),
      company: formData.company,
      country: formData.country,
      quantity: formData.quantity,
      oil_type: formData.oilType,
      message: formData.message,
      reply_to: formData.email,
    };

    try {
      await emailjs.send(
        "service_zanq9cr",
        "template_vt8fst9",
        templateParams,
        "wmEA3TtXIC6dacsNf"
      );

      setIsSubmitting(false);
      setSubmitSuccess(true);
      clearForm();

      Notiflix.Report.success(
        "Success!",
        "Your inquiry has been sent successfully. We'll get back to you soon.",
        "Okay",
        {
          width: "420px",
          borderRadius: "16px",
          svgSize: "60px",
        }
      );
    } catch (error) {
      console.error("Email sending error:", error);
      setIsSubmitting(false);
      Notiflix.Report.failure(
        "Error",
        "Failed to send your inquiry. Please check your connection and try again.",
        "Okay",
        {
          width: "420px",
          borderRadius: "16px",
        }
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff6e3] via-white to-[#fff6e3] text-black overflow-hidden">
      <NavBar />
      <Helmet>
        <title>Contact Solivera - Request Your Olive Oil Supply</title>
        <meta
          name="description"
          content="Contact Solivera to request your olive oil supply. Fill out the form to get in touch with us."
        />
        <meta
          property="og:title"
          content="Contact Solivera - Olive Oil Supply"
        />
        <meta
          property="og:description"
          content="Reach out to Solivera to request high-quality olive oil. Fill the form to get started."
        />
        <meta
          property="og:image"
          content="https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=1200"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <section id="form" className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-5xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-green-600 via-green-500 to-yellow-500 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Request Your Olive Oil Supply
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Fill out the form below and we'll get back to you with a quote
              within 24 hours
            </motion.p>
          </motion.div>

          {/* Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="backdrop-blur-xl bg-white/80 rounded-3xl p-6 sm:p-10 md:p-12 border border-white/20 shadow-2xl"
          >
            <form onSubmit={sendEmail} className="space-y-6 sm:space-y-8">
              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="relative">
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleChange("firstName", e.target.value)}
                      placeholder="John"
                      className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                        errors.firstName
                          ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                          : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                      }`}
                    />
                    <User
                      className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                        errors.firstName ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <AnimatePresence>
                    {errors.firstName && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.firstName}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                <div className="relative">
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleChange("lastName", e.target.value)}
                      placeholder="Doe"
                      className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                        errors.lastName
                          ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                          : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                      }`}
                    />
                    <User
                      className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                        errors.lastName ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <AnimatePresence>
                    {errors.lastName && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.lastName}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Email Field */}
              <div className="relative">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="john.doe@example.com"
                    className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                      errors.email
                        ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                        : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                    }`}
                  />
                  <Mail
                    className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                      errors.email ? "text-red-400" : "text-gray-400"
                    }`}
                  />
                </div>
                <AnimatePresence>
                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {errors.email}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Phone Field */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <CountryCodeDropdown
                    countryCodes={countryCodes}
                    countryCode={countryCode}
                    setCountryCode={setCountryCode}
                  />
                  <div className="flex-1 relative">
                    <input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        handleChange("phone", e.target.value.replace(/\D/g, ""))
                      }
                      placeholder="123456789"
                      className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                        errors.phone
                          ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                          : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                      }`}
                    />
                    <Phone
                      className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                        errors.phone ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                </div>
                <AnimatePresence>
                  {errors.phone && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {errors.phone}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Company and Country */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="relative">
                  <label
                    htmlFor="company"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleChange("company", e.target.value)}
                      placeholder="Company Ltd."
                      className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                        errors.company
                          ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                          : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                      }`}
                    />
                    <Building2
                      className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                        errors.company ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <AnimatePresence>
                    {errors.company && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.company}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                <div className="relative">
                  <label
                    htmlFor="country"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Country of Export <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="country"
                      name="country"
                      type="text"
                      value={formData.country}
                      onChange={(e) => handleChange("country", e.target.value)}
                      placeholder="Tunisia"
                      className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                        errors.country
                          ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                          : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                      }`}
                    />
                    <Globe
                      className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                        errors.country ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <AnimatePresence>
                    {errors.country && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.country}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Quantity and Oil Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="relative">
                  <label
                    htmlFor="quantity"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Quantity (liters) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="quantity"
                      name="quantity"
                      type="number"
                      min="1"
                      step="0.01"
                      value={formData.quantity}
                      onChange={(e) => handleChange("quantity", e.target.value)}
                      placeholder="1000"
                      className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                        errors.quantity
                          ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                          : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                      }`}
                    />
                    <Box
                      className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                        errors.quantity ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <AnimatePresence>
                    {errors.quantity && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.quantity}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                <div className="relative">
                  <label
                    htmlFor="oilType"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Olive Oil Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="oilType"
                      name="oilType"
                      type="text"
                      value={formData.oilType}
                      onChange={(e) => handleChange("oilType", e.target.value)}
                      placeholder="Extra Virgin, Virgin, Pomace"
                      className={`w-full pl-12 pr-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 ${
                        errors.oilType
                          ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                          : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                      }`}
                    />
                    <Sparkles
                      className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                        errors.oilType ? "text-red-400" : "text-gray-400"
                      }`}
                    />
                  </div>
                  <AnimatePresence>
                    {errors.oilType && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                      >
                        <AlertCircle className="w-4 h-4" />
                        {errors.oilType}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Message Field */}
              <div className="relative">
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Message / Details <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  placeholder="Please provide any additional details about your order..."
                  rows="5"
                  className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl focus:outline-none focus:ring-2 transition-all text-black placeholder-gray-400 resize-y min-h-[140px] ${
                    errors.message
                      ? "border-red-400 focus:ring-red-400 focus:border-red-500"
                      : "border-gray-200 focus:ring-green-400 focus:border-green-500"
                  }`}
                />
                <AnimatePresence>
                  {errors.message && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="mt-1.5 text-sm text-red-500 flex items-center gap-1"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {errors.message}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* ReCAPTCHA */}
              <div className="flex flex-col items-center gap-4">
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LfuZhQsAAAAALemOW4yRrMjOf7xMPXBfMlW8QrV"
                    onChange={(value) => {
                      setIsHuman(!!value);
                      if (errors.isHuman) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.isHuman;
                          return newErrors;
                        });
                      }
                    }}
                    onExpired={() => {
                      setIsHuman(false);
                    }}
                    onError={() => {
                      setIsHuman(false);
                      Notiflix.Notify.warning(
                        "ReCAPTCHA error. Please refresh the page.",
                        {
                          position: "right-top",
                          timeout: 3000,
                        }
                      );
                    }}
                  />
                </div>
                <AnimatePresence>
                  {errors.isHuman && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-sm text-red-500 flex items-center gap-1"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {errors.isHuman}
                    </motion.p>
                  )}
                </AnimatePresence>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  className={`w-full sm:w-auto px-10 py-4 bg-gradient-to-r from-green-500 to-green-600 rounded-xl text-white font-semibold text-lg uppercase tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 ${
                    isSubmitting
                      ? "opacity-75 cursor-not-allowed"
                      : "hover:from-green-600 hover:to-green-700"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Inquiry
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {isVisible && (
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-6 sm:bottom-8 right-6 sm:right-8 p-4 rounded-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-2xl transition-all duration-300 z-50 border-2 border-white"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Scroll to top"
          >
            <ArrowRight className="w-6 h-6 rotate-[-90deg]" />
          </motion.button>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Contact;
